import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router';
import type { ApiError, PromotionRow } from '../api';
import { promotionEndpoints } from '../api';
import { NotPermitted } from '../App';
import { usePermissions } from '../session';
import { Badge, Button, EmptyRow, ErrorNote, Field, Panel, Table, formatWhen, statusTone } from '../ui';
import { INPUT, dollars, humanize } from './event/shared';
import { PromoCodeBatches } from './PromoCodeBatches';
import { PromoCodeRestrictions } from './PromoCodeRestrictions';

/**
 * Promo codes (D-143): the discount-code system, called "Promotions" until
 * D-377 gave that name to the library of theme nights and specials. Only the
 * words and the address changed (`/promo-codes`, with `/promotions/:id`
 * forwarded here); the API, the permission and the tables keep their names.
 *
 * A standalone page rather than an event tab, because a promotion may be
 * cross-event — `promotions.event_id` is nullable and a code good at every show
 * is a real thing operators run.
 *
 * The form only ever offers the amount field the chosen type actually uses. The
 * table has a CHECK per type, so offering all four at once would mean showing
 * three fields that cannot be saved together, and a promotion that cannot be
 * priced is refused at creation rather than at somebody's checkout.
 */

const TYPES = [
  { code: 'PERCENT_OFF', label: 'Percentage off', field: 'discount_basis_points' },
  { code: 'AMOUNT_OFF', label: 'Amount off', field: 'discount_amount_cents' },
  { code: 'FIXED_PRICE', label: 'Fixed price per ticket', field: 'fixed_price_cents' },
  { code: 'BUY_X_GET_Y', label: 'Buy X get Y', field: 'buy_x_get_y' },
  { code: 'ACCESS_ONLY', label: 'Access only (unlocks a presale)', field: null },
] as const;

const describe = (promotion: PromotionRow): string => {
  switch (promotion.promotion_type) {
    case 'PERCENT_OFF':
      return `${(promotion.discount_basis_points ?? 0) / 100}% off`;
    case 'AMOUNT_OFF':
      return `${dollars(promotion.discount_amount_cents ?? 0)} off ${promotion.discount_scope === 'PER_ORDER' ? 'the order' : 'each ticket'}`;
    case 'FIXED_PRICE':
      return `${dollars(promotion.fixed_price_cents ?? 0)} per ticket`;
    case 'BUY_X_GET_Y':
      return `Buy ${promotion.buy_quantity}, get ${promotion.get_quantity} at ${(promotion.get_discount_basis_points ?? 0) / 100}% off`;
    case 'ACCESS_ONLY':
      return 'Unlocks a presale, no discount';
    default:
      return humanize(promotion.promotion_type);
  }
};

export default function PromoCodesPage() {
  const { id } = useParams();
  const can = usePermissions();
  if (!can('promotions.manage')) return <NotPermitted permission="promotions.manage" />;
  return id ? <PromotionDetail id={id} /> : <PromotionList />;
}

const blank = { code: '', name: '', promotion_type: 'PERCENT_OFF', amount: '', discount_scope: 'PER_TICKET' as 'PER_TICKET' | 'PER_ORDER', buy_quantity: '', get_quantity: '', get_percent: '', usage_cap_total: '' };

function PromotionList() {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ ...blank });
  const [status, setStatus] = useState('');

  // Automatic deals are managed on their promotion (D-377), so they are not listed here.
  const promotions = useQuery({ queryKey: ['promotions', status], queryFn: () => promotionEndpoints.list({ automatic: '0', ...(status ? { status } : {}) }) });
  const chosen = TYPES.find((type) => type.code === form.promotion_type)!;

  const create = useMutation({
    mutationFn: () => {
      const amount = Number(form.amount || 0);
      return promotionEndpoints.create({
        code: form.code.trim(),
        name: form.name.trim(),
        promotion_type: form.promotion_type,
        ...(form.promotion_type === 'PERCENT_OFF' ? { discount_basis_points: Math.round(amount * 100) } : {}),
        ...(form.promotion_type === 'AMOUNT_OFF' ? { discount_amount_cents: Math.round(amount * 100), discount_scope: form.discount_scope } : {}),
        ...(form.promotion_type === 'FIXED_PRICE' ? { fixed_price_cents: Math.round(amount * 100) } : {}),
        ...(form.promotion_type === 'BUY_X_GET_Y'
          ? { buy_quantity: Number(form.buy_quantity), get_quantity: Number(form.get_quantity), get_discount_basis_points: Math.round(Number(form.get_percent) * 100) }
          : {}),
        ...(form.usage_cap_total ? { usage_cap_total: Number(form.usage_cap_total) } : {}),
      });
    },
    onSuccess: () => {
      setCreating(false);
      setForm({ ...blank });
      void queryClient.invalidateQueries({ queryKey: ['promotions'] });
    },
    onError: (cause: ApiError) => setError(cause.message),
  });

  return (
    <div className="space-y-4">
      {error ? <ErrorNote>{error}</ErrorNote> : null}
      <Panel
        title="Promo codes"
        description="A promo code is a discount somebody was given, as opposed to a price the venue offers everybody. Make the discount here, then the codes customers type inside it. Nothing redeems while it is a draft. Theme nights, specials and discounts that need no code are on the Promotions page."
        actions={
          <span className="flex items-center gap-2">
            <select className={INPUT} value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Filter by status">
              <option value="">All statuses</option>
              {['DRAFT', 'ACTIVE', 'PAUSED', 'ENDED'].map((value) => (
                <option key={value} value={value}>
                  {humanize(value)}
                </option>
              ))}
            </select>
            {!creating ? <Button onClick={() => setCreating(true)}>New promo code discount</Button> : null}
          </span>
        }
      >
        {creating ? (
          <form
            className="grid gap-3 border-t border-line px-5 py-4 md:grid-cols-2"
            onSubmit={(e) => {
              e.preventDefault();
              create.mutate();
            }}
          >
            <Field label="Name" htmlFor="promo-name" hint="What staff call it.">
              <input id="promo-name" className={INPUT} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required maxLength={120} />
            </Field>
            <Field label="Reference" htmlFor="promo-code" hint="An internal label. Not the code customers type — those are made once this exists.">
              <input id="promo-code" className={INPUT} value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} required maxLength={40} />
            </Field>
            <Field label="What it does" htmlFor="promo-type">
              <select id="promo-type" className={INPUT} value={form.promotion_type} onChange={(e) => setForm({ ...form, promotion_type: e.target.value })}>
                {TYPES.map((type) => (
                  <option key={type.code} value={type.code}>
                    {type.label}
                  </option>
                ))}
              </select>
            </Field>
            {chosen.field === 'discount_basis_points' ? (
              <Field label="Percentage off" htmlFor="promo-amount">
                <input id="promo-amount" type="number" min={0.01} max={100} step={0.01} className={INPUT} value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} required />
              </Field>
            ) : null}
            {chosen.field === 'discount_amount_cents' ? (
              <>
                <Field label="Off what" htmlFor="promo-scope">
                  <select id="promo-scope" className={INPUT} value={form.discount_scope} onChange={(e) => setForm({ ...form, discount_scope: e.target.value as 'PER_TICKET' | 'PER_ORDER' })}>
                    <option value="PER_TICKET">Each ticket</option>
                    <option value="PER_ORDER">The whole order</option>
                  </select>
                </Field>
                {/* The label follows the choice: "dollars off each ticket" beside
                    a per-order toggle is how somebody configures the wrong one (D-163). */}
                <Field
                  label={form.discount_scope === 'PER_ORDER' ? 'Dollars off the order' : 'Dollars off each ticket'}
                  htmlFor="promo-amount"
                  hint={form.discount_scope === 'PER_ORDER' ? 'One amount, shared across the tickets by price. Never more than the order is worth.' : 'Taken off every eligible ticket.'}
                >
                  <input id="promo-amount" type="number" min={0.01} step={0.01} className={INPUT} value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} required />
                </Field>
              </>
            ) : null}
            {chosen.field === 'fixed_price_cents' ? (
              <Field label="Price per ticket" htmlFor="promo-amount" hint="Replaces the ticket's price. Fees are still added.">
                <input id="promo-amount" type="number" min={0} step={0.01} className={INPUT} value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} required />
              </Field>
            ) : null}
            {chosen.field === 'buy_x_get_y' ? (
              <>
                <Field label="Buy this many" htmlFor="promo-buy">
                  <input id="promo-buy" type="number" min={1} className={INPUT} value={form.buy_quantity} onChange={(e) => setForm({ ...form, buy_quantity: e.target.value })} required />
                </Field>
                <Field label="Get this many" htmlFor="promo-get">
                  <input id="promo-get" type="number" min={1} className={INPUT} value={form.get_quantity} onChange={(e) => setForm({ ...form, get_quantity: e.target.value })} required />
                </Field>
                <Field label="Discount on those" htmlFor="promo-get-percent" hint="100 means free. The cheapest tickets in each group are the discounted ones.">
                  <input id="promo-get-percent" type="number" min={1} max={100} className={INPUT} value={form.get_percent} onChange={(e) => setForm({ ...form, get_percent: e.target.value })} required />
                </Field>
              </>
            ) : null}
            <Field label="Total ticket cap" htmlFor="promo-cap" hint="Optional. How many tickets this promotion may ever discount.">
              <input id="promo-cap" type="number" min={1} className={INPUT} value={form.usage_cap_total} onChange={(e) => setForm({ ...form, usage_cap_total: e.target.value })} />
            </Field>
            <div className="flex items-end gap-2">
              <Button type="submit" variant="primary" disabled={create.isPending}>
                {create.isPending ? 'Saving…' : 'Create as draft'}
              </Button>
              <Button type="button" onClick={() => { setCreating(false); setForm({ ...blank }); }}>
                Cancel
              </Button>
            </div>
          </form>
        ) : null}
      </Panel>

      <Panel title="All promo code discounts">
        <Table headers={['Name', 'Reference', 'What it does', 'Status', 'Codes', 'Used', 'Funded by', '']}>
          {(promotions.data?.promotions ?? []).length === 0 ? (
            <EmptyRow columns={8}>No promo codes yet.</EmptyRow>
          ) : (
            (promotions.data?.promotions ?? []).map((promotion) => (
              <tr key={promotion.id} className="border-b border-line last:border-0">
                <td className="px-5 py-3 text-sm font-medium">
                  <Link to={`/promo-codes/${promotion.id}`} className="text-brand hover:underline">
                    {promotion.name}
                  </Link>
                </td>
                <td className="px-5 py-3 text-sm text-ink-500">{promotion.code}</td>
                <td className="px-5 py-3 text-sm">{describe(promotion)}</td>
                <td className="px-5 py-3 text-sm">
                  <Badge tone={statusTone(promotion.status)}>{humanize(promotion.status)}</Badge>
                </td>
                <td className="px-5 py-3 text-sm tabular">{promotion.codes ?? 0}</td>
                <td className="px-5 py-3 text-sm tabular">
                  {promotion.used ?? 0}
                  {promotion.usage_cap_total ? ` / ${promotion.usage_cap_total}` : ''}
                </td>
                <td className="px-5 py-3 text-sm text-ink-500">{humanize(promotion.funded_by_owner_type)}</td>
                <td className="px-5 py-3 text-right text-sm">
                  <Link to={`/promo-codes/${promotion.id}`} className="text-brand hover:underline">
                    Open
                  </Link>
                </td>
              </tr>
            ))
          )}
        </Table>
      </Panel>
    </div>
  );
}

function PromotionDetail({ id }: { id: string }) {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [codeText, setCodeText] = useState('');
  const [codeCap, setCodeCap] = useState('');

  const detail = useQuery({ queryKey: ['promotion', id], queryFn: () => promotionEndpoints.get(id) });
  const redemptions = useQuery({ queryKey: ['promotion-redemptions', id], queryFn: () => promotionEndpoints.redemptions(id) });
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['promotion', id] });
    void queryClient.invalidateQueries({ queryKey: ['promotions'] });
  };
  const fail = (cause: ApiError) => setError(cause.message);

  const setStatus = useMutation({ mutationFn: (status: string) => promotionEndpoints.update(id, { status }), onSuccess: refresh, onError: fail });
  const addCode = useMutation({
    mutationFn: () => promotionEndpoints.createCode(id, { code: codeText.trim(), ...(codeCap ? { usage_cap: Number(codeCap) } : {}) }),
    onSuccess: () => {
      setCodeText('');
      setCodeCap('');
      refresh();
    },
    onError: fail,
  });
  const toggleCode = useMutation({ mutationFn: (input: { codeId: string; active: boolean }) => promotionEndpoints.updateCode(id, input.codeId, { active: input.active }), onSuccess: refresh, onError: fail });

  const promotion = detail.data?.promotion;
  if (!promotion) return <p className="text-sm text-ink-500">Loading…</p>;

  return (
    <div className="space-y-4">
      {error ? <ErrorNote>{error}</ErrorNote> : null}
      <Panel
        title={promotion.name}
        description={`${describe(promotion)} · funded by ${humanize(promotion.funded_by_owner_type)}${promotion.automatic ? ' · an automatic deal of a promotion: managed on the Promotions page' : ''}`}
        actions={
          <span className="flex items-center gap-2">
            <Link to="/promo-codes" className="text-sm text-brand hover:underline">
              All promo codes
            </Link>
            {promotion.status === 'DRAFT' || promotion.status === 'PAUSED' ? (
              <Button variant="primary" onClick={() => setStatus.mutate('ACTIVE')} disabled={setStatus.isPending}>
                Activate
              </Button>
            ) : null}
            {promotion.status === 'ACTIVE' ? <Button onClick={() => setStatus.mutate('PAUSED')} disabled={setStatus.isPending}>Pause</Button> : null}
            {promotion.status !== 'ENDED' ? (
              <Button
                onClick={() => {
                  if (window.confirm('End this promotion? Its codes stop working and it cannot be reactivated.')) setStatus.mutate('ENDED');
                }}
                disabled={setStatus.isPending}
              >
                End
              </Button>
            ) : null}
          </span>
        }
      >
        <dl className="grid gap-x-8 gap-y-2 px-5 py-4 text-sm sm:grid-cols-2">
          <Fact label="Status">
            <Badge tone={statusTone(promotion.status)}>{humanize(promotion.status)}</Badge>
          </Fact>
          <Fact label="Reference">{promotion.code}</Fact>
          <Fact label="Runs">{promotion.starts_at ? `${formatWhen(promotion.starts_at)} — ${formatWhen(promotion.ends_at)}` : 'Whenever it is active'}</Fact>
          <Fact label="Applies to">{promotion.event_id ? 'One event' : 'Every event'}</Fact>
          <Fact label="Who may use it">{humanize(promotion.eligibility_mode)}</Fact>
          <Fact label="Total cap">{promotion.usage_cap_total ?? 'No cap'}</Fact>
        </dl>
      </Panel>

      <Panel
        title="Shared codes"
        description="What a customer types. A code is stored in capitals however it is entered, and cannot be renamed once it exists — it has been printed somewhere. Retire it instead."
      >
        <form
          className="flex flex-wrap items-end gap-3 border-t border-line px-5 py-4"
          onSubmit={(e) => {
            e.preventDefault();
            addCode.mutate();
          }}
        >
          <Field label="New code" htmlFor="promo-new-code">
            <input id="promo-new-code" className={INPUT} value={codeText} onChange={(e) => setCodeText(e.target.value)} placeholder="MEMBERS20" minLength={3} maxLength={40} required />
          </Field>
          <Field label="Cap for this code" htmlFor="promo-new-cap" hint="Optional, on top of the promotion's own cap.">
            <input id="promo-new-cap" type="number" min={1} className={INPUT} value={codeCap} onChange={(e) => setCodeCap(e.target.value)} />
          </Field>
          <Button type="submit" variant="primary" disabled={addCode.isPending || codeText.trim().length < 3}>
            {addCode.isPending ? 'Adding…' : 'Add code'}
          </Button>
        </form>
        <Table headers={['Code', 'Used', 'Cap', 'Status', '']}>
          {(detail.data?.codes ?? []).length === 0 ? (
            <EmptyRow columns={5}>No shared codes. Nobody can redeem this promotion without a shared code or a single-use one below.</EmptyRow>
          ) : (
            (detail.data?.codes ?? []).map((code) => (
              <tr key={code.id} className="border-b border-line last:border-0">
                <td className="px-5 py-3 font-mono text-sm">{code.code_display}</td>
                <td className="px-5 py-3 text-sm tabular">{code.used ?? 0}</td>
                <td className="px-5 py-3 text-sm tabular">{code.usage_cap ?? '—'}</td>
                <td className="px-5 py-3 text-sm">
                  <Badge tone={code.active ? 'ok' : 'neutral'}>{code.active ? 'Active' : 'Retired'}</Badge>
                </td>
                <td className="px-5 py-3 text-right text-sm">
                  <Button onClick={() => toggleCode.mutate({ codeId: code.id, active: !code.active })} disabled={toggleCode.isPending}>
                    {code.active ? 'Retire' : 'Reinstate'}
                  </Button>
                </td>
              </tr>
            ))
          )}
        </Table>
      </Panel>

      <PromoCodeRestrictions promotionId={id} restrictions={detail.data?.restrictions ?? null} onChanged={refresh} />

      <PromoCodeBatches promotionId={id} onChanged={refresh} />

      <Panel title="Redemptions" description="Every order that used this promotion. Reserved means a checkout is open; committed means it was paid for.">
        <Table headers={['When', 'Order', 'Code', 'Tickets', 'Discount', 'Status']}>
          {(redemptions.data?.redemptions ?? []).length === 0 ? (
            <EmptyRow columns={6}>Not used yet.</EmptyRow>
          ) : (
            (redemptions.data?.redemptions ?? []).map((row) => (
              <tr key={String(row['id'])} className="border-b border-line last:border-0">
                <td className="px-5 py-3 text-sm">{formatWhen(String(row['created_at']))}</td>
                <td className="px-5 py-3 text-sm">{String(row['order_number'] ?? '—')}</td>
                <td className="px-5 py-3 font-mono text-sm">{String(row['code_display'] ?? '—')}</td>
                <td className="px-5 py-3 text-sm tabular">{Number(row['quantity'] ?? 0)}</td>
                <td className="px-5 py-3 text-sm tabular">{dollars(Number(row['discount_cents'] ?? 0))}</td>
                <td className="px-5 py-3 text-sm">
                  <Badge tone={row['status'] === 'COMMITTED' ? 'ok' : row['status'] === 'RESERVED' ? 'accent' : 'neutral'}>{humanize(String(row['status']))}</Badge>
                </td>
              </tr>
            ))
          )}
        </Table>
      </Panel>
    </div>
  );
}

function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex justify-between gap-4 border-b border-line py-1.5">
      <dt className="text-ink-500">{label}</dt>
      <dd className="text-right font-medium">{children}</dd>
    </div>
  );
}
